import { addDoc, collection, increment, serverTimestamp, Timestamp } from "firebase/firestore";
import { auth, db } from "../firebase/config";

export const ANALYTICS_V2_DAILY_COLLECTION = "analyticsDailyV2";
export const ANALYTICS_V2_INTAKE_COLLECTION = "analyticsIntakeV2";
export const ANALYTICS_V2_SCHEMA_VERSION = 2;
export const ANALYTICS_V2_SESSION_KEY = "mfp:analytics:v2:session";
export const ANALYTICS_V2_LAST_PAGE_VIEW_KEY = "mfp:analytics:v2:last-page-view";
export const ANALYTICS_V2_DEDUPE_KEY = "mfp:analytics:v2:dedupe";
export const ANALYTICS_V2_PENDING_KEY = "mfp:analytics:v2:pending";
export const ANALYTICS_V2_SESSION_IDLE_MS = 30 * 60 * 1000;
export const ANALYTICS_V2_PAGE_VIEW_DEDUPE_MS = 3000;
export const ANALYTICS_V2_FLUSH_DEBOUNCE_MS = 5000;
export const ANALYTICS_V2_MAX_PENDING_EVENTS = 8;
export const ANALYTICS_V2_RAW_RETENTION_DAYS = 30;
export const ANALYTICS_V2_INTAKE_RETENTION_DAYS = 14;
export const ANALYTICS_V2_MAX_CAMPAIGN_KEY_LENGTH = 48;

export const CANONICAL_ANALYTICS_EVENTS = [
  "session_start",
  "page_view",
  "feature_open",
  "registration_start",
  "registration_complete",
  "first_action",
  "conversion",
  "error",
];

export const LEGACY_FIRST_ACTION_EVENTS = [
  "add_product_completed",
  "farm_stand_completed",
  "job_post_completed",
  "community_post_completed",
  "farm_records_first_action",
];

export const ANALYTICS_V2_SOURCE_KEYS = [
  "direct",
  "facebook",
  "google",
  "campaign",
  "referral",
];

export const ANALYTICS_V2_CAMPAIGN_KEYS = ["campaign"];

export const ANALYTICS_V2_PAGE_KEYS = [
  "home",
  "auth",
  "legal",
  "farm_help",
  "farm_public",
  "fall_crawl",
  "local_live",
  "local_live_participant",
  "marketplace",
  "marketplace_detail",
  "events",
  "farm_stands",
  "stand_public",
  "jobs",
  "plants_garden",
  "farm_records",
  "land_share",
  "community",
  "profile",
  "admin",
  "content_studio",
  "other",
];

export const ANALYTICS_V2_FEATURE_KEYS = [
  "activation",
  "admin",
  "auth",
  "billy",
  "campaign",
  "community",
  "error",
  "events",
  "fall_crawl",
  "farm_help",
  "farm_records",
  "farm_stand",
  "jobs",
  "land_share",
  "live_shelf",
  "local_live",
  "marketplace",
  "navigation",
  "pickup_spot",
  "plants_garden",
  "profile",
  "public",
  "registration",
  "other",
];

export const ANALYTICS_V2_ERROR_KEYS = [
  "unknown",
  "form_validation",
  "permission_denied",
  "network",
  "not_found",
  "error",
  "other",
];

const INTERNAL_HOSTS = new Set([
  "myfarmingpal.com",
  "www.myfarmingpal.com",
  "hay-marketplace.web.app",
  "hay-marketplace.firebaseapp.com",
  "hay-marketplace-e45da.web.app",
  "hay-marketplace-e45da.firebaseapp.com",
  "localhost",
  "127.0.0.1",
]);

const SENSITIVE_PAYLOAD_KEYS = new Set([
  "ip",
  "ipAddress",
  "latitude",
  "longitude",
  "lat",
  "lng",
  "gps",
  "location",
  "email",
  "phone",
  "phoneNumber",
  "advertisingId",
  "adId",
  "fingerprint",
  "userAgent",
  "uid",
  "userId",
]);

const EVENT_COUNTERS = {
  registration_start: "registrationStarts",
  registration_complete: "registrationCompletes",
  first_action: "firstActionUsers",
  conversion: "conversions",
  error: "errors",
};

const EXACT_PAGE_KEYS = {
  "/": "home",
  "/home": "home",
  "/login": "auth",
  "/register": "auth",
  "/subscribe": "auth",
  "/terms": "legal",
  "/privacy": "legal",
  "/community-rules": "legal",
  "/safety": "legal",
  "/contact": "legal",
  "/farm-help": "farm_help",
  "/fall-crawl/2026": "fall_crawl",
  "/marketplace": "marketplace",
  "/events": "events",
  "/farm-stands": "farm_stands",
  "/my-farm": "profile",
  "/jobs": "jobs",
  "/plants-garden": "plants_garden",
  "/plants-garden/new": "plants_garden",
  "/almanac": "other",
  "/farm-records": "farm_records",
  "/records": "farm_records",
  "/animal-records": "farm_records",
  "/land-share": "land_share",
  "/map": "other",
  "/community": "community",
  "/community/alerts": "community",
  "/inbox": "other",
  "/add-product": "marketplace",
  "/add-farm-stand": "farm_stands",
  "/add-event": "events",
  "/edit-profile": "profile",
  "/browse-users": "profile",
  "/browse": "profile",
  "/admin": "admin",
  "/admin/local-live": "admin",
  "/content-studio": "content_studio",
};

const getNow = () => Date.now();

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const getDateId = (now = new Date()) => new Date(now).toISOString().slice(0, 10);

const sanitizeString = (value = "", maxLength = 120) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .slice(0, maxLength);

export const sanitizeAnalyticsKey = (value = "unknown", fallback = "unknown", maxLength = 80) => {
  const key = String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, maxLength);
  return key || fallback;
};

export const sanitizeCampaignKey = (value = "") =>
  sanitizeAnalyticsKey(value, "", ANALYTICS_V2_MAX_CAMPAIGN_KEY_LENGTH);

const normalizeKnownKey = (value, knownKeys, fallback = "other") => {
  const key = sanitizeAnalyticsKey(value, fallback);
  return knownKeys.includes(key) ? key : fallback;
};

const isInternalHostname = (hostname = "", currentHostname = "") => {
  const clean = String(hostname || "").toLowerCase();
  if (!clean) return false;
  if (clean === String(currentHostname || "").toLowerCase()) return true;
  return INTERNAL_HOSTS.has(clean);
};

const parseUrl = (value = "") => {
  try {
    return value ? new URL(value) : null;
  } catch {
    return null;
  }
};

const isFacebookValue = (value = "") => /(^|[^a-z])(fb|facebook|instagram)([^a-z]|$)/i.test(String(value || ""));
const isGoogleValue = (value = "") => /(^|\.)google\./i.test(String(value || "")) || /google/i.test(String(value || ""));

export const normalizeAnalyticsSource = ({
  search = "",
  referrer = "",
  currentHostname = typeof window !== "undefined" ? window.location.hostname : "",
  previousSource = "",
} = {}) => {
  const params = new URLSearchParams(String(search || "").replace(/^\?/, ""));
  const utmSource = sanitizeString(params.get("utm_source"), 80).toLowerCase();
  const utmMedium = sanitizeString(params.get("utm_medium"), 80).toLowerCase();
  const utmCampaign = sanitizeString(params.get("utm_campaign"), 120).toLowerCase();
  const utmContent = sanitizeString(params.get("utm_content"), 120).toLowerCase();
  const referrerUrl = parseUrl(referrer);
  const referrerHostname = sanitizeString(referrerUrl?.hostname || "", 120).toLowerCase();
  const hasUtm = Boolean(utmSource || utmMedium || utmCampaign || utmContent);
  const internalReferrer = isInternalHostname(referrerHostname, currentHostname);

  if (internalReferrer && previousSource) {
    return {
      source: previousSource,
      hostname: "",
      campaign: "",
      isInternalNavigation: true,
    };
  }

  if (isFacebookValue(`${utmSource} ${utmCampaign}`) || referrerHostname.includes("facebook.com")) {
    return {
      source: "facebook",
      hostname: referrerHostname && !internalReferrer ? referrerHostname : "",
      campaign: sanitizeCampaignKey(utmCampaign),
      isInternalNavigation: internalReferrer,
    };
  }

  if (isGoogleValue(utmSource) || isGoogleValue(referrerHostname)) {
    return {
      source: "google",
      hostname: referrerHostname && !internalReferrer ? referrerHostname : "",
      campaign: sanitizeCampaignKey(utmCampaign),
      isInternalNavigation: internalReferrer,
    };
  }

  if (hasUtm) {
    return {
      source: "campaign",
      hostname: referrerHostname && !internalReferrer ? referrerHostname : "",
      campaign: sanitizeCampaignKey(utmCampaign || utmSource || "campaign"),
      isInternalNavigation: internalReferrer,
    };
  }

  if (referrerHostname && !internalReferrer) {
    return {
      source: "referral",
      hostname: referrerHostname,
      campaign: "",
      isInternalNavigation: false,
    };
  }

  return {
    source: previousSource || "direct",
    hostname: "",
    campaign: "",
    isInternalNavigation: internalReferrer,
  };
};

const createSessionId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `s_${crypto.randomUUID()}`;
  }
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
};

const readJson = (storage, key, fallback) => {
  try {
    const parsed = JSON.parse(storage?.getItem(key) || "null");
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (storage, key, value) => {
  try {
    storage?.setItem(key, JSON.stringify(value));
  } catch {}
};

const readSession = (storage) => readJson(storage, ANALYTICS_V2_SESSION_KEY, null);
const writeSession = (storage, session) => writeJson(storage, ANALYTICS_V2_SESSION_KEY, session);

export const getAnalyticsV2Session = ({
  now = getNow(),
  storage = typeof window !== "undefined" ? window.sessionStorage : null,
  search = typeof window !== "undefined" ? window.location.search : "",
  referrer = typeof document !== "undefined" ? document.referrer : "",
  currentHostname = typeof window !== "undefined" ? window.location.hostname : "",
} = {}) => {
  const current = readSession(storage);
  const expired = !current?.id || now - Number(current.lastSeenAt || 0) > ANALYTICS_V2_SESSION_IDLE_MS;

  if (!expired) {
    const source = normalizeAnalyticsSource({
      search,
      referrer,
      currentHostname,
      previousSource: current.acquisitionSource || "direct",
    });
    const next = {
      ...current,
      acquisitionSource: source.source || current.acquisitionSource || "direct",
      acquisitionHostname: current.acquisitionHostname || source.hostname || "",
      campaign: current.campaign || source.campaign || "",
      lastSeenAt: now,
    };
    writeSession(storage, next);
    return { ...next, isNew: false };
  }

  const source = normalizeAnalyticsSource({ search, referrer, currentHostname });
  const next = {
    id: createSessionId(),
    acquisitionSource: source.source,
    acquisitionHostname: source.hostname,
    campaign: source.campaign,
    startedAt: now,
    lastSeenAt: now,
    countedDates: {},
  };
  writeSession(storage, next);
  return { ...next, isNew: true };
};

const safePath = (value = "/") => {
  const path = sanitizeString(value || "/", 200).split("?")[0] || "/";
  return path.startsWith("/") ? path : "/";
};

export const normalizeAnalyticsPageKey = (value = "/") => {
  const path = safePath(value).toLowerCase().replace(/\/+$/, "") || "/";
  if (EXACT_PAGE_KEYS[path]) return EXACT_PAGE_KEYS[path];
  if (/^\/farm\/[^/]+$/.test(path)) return "farm_public";
  if (/^\/stand\/[^/]+$/.test(path)) return "stand_public";
  if (/^\/profile\/[^/]+$/.test(path)) return "profile";
  if (/^\/chat\/[^/]+$/.test(path)) return "other";
  if (/^\/local-live\/[^/]+\/participant\/[^/]+$/.test(path)) return "local_live_participant";
  if (/^\/local-live\/[^/]+$/.test(path)) return "local_live";
  if (/^\/admin\/local-live(\/[^/]+)?(\/(homepage-preview|map-preview|backroads-book-test))?$/.test(path)) return "admin";
  if (/^\/marketplace\/[^/]+$/.test(path)) return "marketplace_detail";
  if (/^\/products?\/[^/]+$/.test(path)) return "marketplace_detail";
  if (/^\/items?\/[^/]+$/.test(path)) return "marketplace_detail";
  return "other";
};

export const normalizeAnalyticsFeatureKey = (value = "other") =>
  normalizeKnownKey(value, ANALYTICS_V2_FEATURE_KEYS, "other");

const normalizeSourceKey = (value = "direct") =>
  normalizeKnownKey(value, ANALYTICS_V2_SOURCE_KEYS, "direct");

const normalizeErrorKey = (value = "unknown") =>
  normalizeKnownKey(value, ANALYTICS_V2_ERROR_KEYS, "other");

const normalizeCampaignAggregateKey = (event = {}) =>
  event.campaign || event.source === "campaign" ? "campaign" : "";

const sanitizeCohortToken = (value = "") =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-f0-9]/g, "")
    .slice(0, 64);

const toHex = (buffer) =>
  Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");

export const getActivationCohortToken = async ({ uid = auth.currentUser?.uid, cohortToken = "" } = {}) => {
  const provided = sanitizeCohortToken(cohortToken);
  if (provided) return provided;
  if (!uid || typeof crypto === "undefined" || !crypto.subtle || typeof TextEncoder === "undefined") {
    return "";
  }
  const input = new TextEncoder().encode(`mfp-activation-v2:${uid}`);
  const digest = await crypto.subtle.digest("SHA-256", input);
  return toHex(digest);
};

export const sanitizeAnalyticsPayload = (payload = {}, eventName = "") => {
  const clean = {};
  Object.entries(payload || {}).forEach(([key, value]) => {
    if (SENSITIVE_PAYLOAD_KEYS.has(key)) return;
    if (value === null || value === undefined) return;
    if (typeof value === "string") clean[key] = sanitizeString(value, 160);
    if (typeof value === "number" && Number.isFinite(value)) clean[key] = Math.round(value);
    if (typeof value === "boolean") clean[key] = value;
  });

  clean.path = normalizeAnalyticsPageKey(payload.path || (typeof window !== "undefined" ? window.location.pathname : "/"));
  if (payload.feature) clean.feature = normalizeAnalyticsFeatureKey(payload.feature);
  if (payload.action) clean.action = sanitizeAnalyticsKey(payload.action, "action", 80);
  if (payload.campaign) clean.campaign = sanitizeCampaignKey(payload.campaign);
  if (eventName === "error") {
    clean.errorCode = normalizeErrorKey(payload.errorCode || "unknown");
    clean.errorSummary = sanitizeString(payload.errorSummary || "error", 120);
  }
  return clean;
};

export const buildCanonicalAnalyticsEvent = (eventName, payload = {}, options = {}) => {
  if (!CANONICAL_ANALYTICS_EVENTS.includes(eventName)) return null;
  const session = options.session || getAnalyticsV2Session(options);
  const safePayload = sanitizeAnalyticsPayload(payload, eventName);
  const source = normalizeAnalyticsSource({
    search: options.search ?? (typeof window !== "undefined" ? window.location.search : ""),
    referrer: options.referrer ?? (typeof document !== "undefined" ? document.referrer : ""),
    currentHostname: options.currentHostname ?? (typeof window !== "undefined" ? window.location.hostname : ""),
    previousSource: session.acquisitionSource,
  });

  return {
    schemaVersion: ANALYTICS_V2_SCHEMA_VERSION,
    eventName,
    createdAt: options.nowDate || new Date(options.now || getNow()),
    sessionId: session.id,
    source: normalizeSourceKey(source.source || session.acquisitionSource || "direct"),
    sourceHostname: source.hostname || session.acquisitionHostname || "",
    campaign: sanitizeCampaignKey(source.campaign || session.campaign || ""),
    isLoggedIn: Boolean(auth.currentUser?.uid),
    ...safePayload,
  };
};

const nestedIncrement = (container, path, amount = 1) => {
  let target = container;
  path.forEach((part, index) => {
    if (index === path.length - 1) {
      target[part] = increment(amount);
      return;
    }
    target[part] = target[part] || {};
    target = target[part];
  });
};

const addCount = (container, path, amount = 1) => {
  let target = container;
  path.forEach((part, index) => {
    if (index === path.length - 1) {
      target[part] = Number(target[part] || 0) + amount;
      return;
    }
    target[part] = target[part] || {};
    target = target[part];
  });
};

const createEmptyAggregate = (dateId) => ({
  date: dateId,
  schemaVersion: ANALYTICS_V2_SCHEMA_VERSION,
  eventCount: 0,
});

const aggregateEvent = (aggregate, event = {}, { includeSessionStart = false } = {}) => {
  const sourceKey = normalizeSourceKey(event.source || "direct");
  const campaignKey = normalizeCampaignAggregateKey(event);
  const pageKey = ANALYTICS_V2_PAGE_KEYS.includes(event.path)
    ? event.path
    : normalizeAnalyticsPageKey(event.path || "/");
  const featureKey = normalizeAnalyticsFeatureKey(event.feature || "other");

  if (includeSessionStart) {
    aggregate.visitors = Number(aggregate.visitors || 0) + 1;
    aggregate.sessions = Number(aggregate.sessions || 0) + 1;
    addCount(aggregate, ["sources", sourceKey, "sessions"]);
    if (campaignKey) addCount(aggregate, ["campaigns", campaignKey, "visits"]);
  }

  if (event.eventName === "page_view") {
    aggregate.pageViews = Number(aggregate.pageViews || 0) + 1;
    addCount(aggregate, ["pages", pageKey, "pageViews"]);
  }

  if (event.eventName === "feature_open") {
    addCount(aggregate, ["features", featureKey, "opens"]);
  }

  const counter = EVENT_COUNTERS[event.eventName];
  if (counter) aggregate[counter] = Number(aggregate[counter] || 0) + 1;

  if (event.eventName === "registration_complete") {
    addCount(aggregate, ["activation", "signupUsers"]);
    aggregate.registrationComplete = true;
  }

  if (event.eventName === "first_action") {
    addCount(aggregate, ["activation", "firstActionUsers"]);
    aggregate.firstAction = true;
  }

  if (event.cohortToken) aggregate.cohortToken = sanitizeCohortToken(event.cohortToken);

  if (event.eventName === "error") {
    addCount(aggregate, ["errorsByCode", normalizeErrorKey(event.errorCode || "unknown"), "count"]);
  }

  aggregate.eventCount = Number(aggregate.eventCount || 0) + 1;
  return aggregate;
};

const aggregateToPatch = (aggregate = {}) => {
  const patch = {
    schemaVersion: ANALYTICS_V2_SCHEMA_VERSION,
    date: aggregate.date,
    updatedAt: serverTimestamp(),
  };
  [
    "visitors",
    "sessions",
    "pageViews",
    "registrationStarts",
    "registrationCompletes",
    "firstActionUsers",
    "conversions",
    "errors",
  ].forEach((key) => {
    const amount = Number(aggregate[key] || 0);
    if (amount > 0) patch[key] = increment(amount);
  });

  Object.entries(aggregate.pages || {}).forEach(([key, value]) => {
    if (ANALYTICS_V2_PAGE_KEYS.includes(key) && Number(value?.pageViews || 0) > 0) {
      nestedIncrement(patch, ["pages", key, "pageViews"], Number(value.pageViews || 0));
    }
  });
  Object.entries(aggregate.sources || {}).forEach(([key, value]) => {
    if (ANALYTICS_V2_SOURCE_KEYS.includes(key) && Number(value?.sessions || 0) > 0) {
      nestedIncrement(patch, ["sources", key, "sessions"], Number(value.sessions || 0));
    }
  });
  Object.entries(aggregate.campaigns || {}).forEach(([key, value]) => {
    if (ANALYTICS_V2_CAMPAIGN_KEYS.includes(key) && Number(value?.visits || 0) > 0) {
      nestedIncrement(patch, ["campaigns", key, "visits"], Number(value.visits || 0));
    }
  });
  Object.entries(aggregate.features || {}).forEach(([key, value]) => {
    if (ANALYTICS_V2_FEATURE_KEYS.includes(key) && Number(value?.opens || 0) > 0) {
      nestedIncrement(patch, ["features", key, "opens"], Number(value.opens || 0));
    }
  });
  Object.entries(aggregate.errorsByCode || {}).forEach(([key, value]) => {
    if (Number(value?.count || 0) > 0) {
      nestedIncrement(patch, ["errorsByCode", normalizeErrorKey(key), "count"], Number(value.count || 0));
    }
  });
  if (Number(aggregate.activation?.signupUsers || 0) > 0) {
    nestedIncrement(patch, ["activation", "signupUsers"], Number(aggregate.activation.signupUsers || 0));
  }
  if (Number(aggregate.activation?.firstActionUsers || 0) > 0) {
    nestedIncrement(patch, ["activation", "firstActionUsers"], Number(aggregate.activation.firstActionUsers || 0));
  }

  return patch;
};

const copyKnownMetricMap = (source = {}, knownKeys = [], metric = "") => {
  const output = {};
  Object.entries(source || {}).forEach(([key, value]) => {
    if (!knownKeys.includes(key)) return;
    const amount = Math.max(0, Math.round(Number(value?.[metric] || 0)));
    if (amount > 0) output[key] = { [metric]: amount };
  });
  return output;
};

export const buildAnalyticsIntakeV2Document = (aggregate = {}, {
  session = {},
  now = getNow(),
} = {}) => {
  const source = normalizeSourceKey(session.acquisitionSource || Object.keys(aggregate.sources || {})[0] || "direct");
  const campaign = sanitizeCampaignKey(session.campaign || (aggregate.campaigns?.campaign ? "campaign" : ""));
  const docPayload = {
    schemaVersion: ANALYTICS_V2_SCHEMA_VERSION,
    date: aggregate.date || getDateId(now),
    sessionId: sanitizeString(session.id || "", 80),
    source,
    sourceHostname: sanitizeString(session.acquisitionHostname || "", 120).toLowerCase(),
    campaign,
    pages: copyKnownMetricMap(aggregate.pages, ANALYTICS_V2_PAGE_KEYS, "pageViews"),
    features: copyKnownMetricMap(aggregate.features, ANALYTICS_V2_FEATURE_KEYS, "opens"),
    errorsByCode: copyKnownMetricMap(aggregate.errorsByCode, ANALYTICS_V2_ERROR_KEYS, "count"),
    visitors: Math.max(0, Math.round(Number(aggregate.visitors || 0))),
    sessions: Math.max(0, Math.round(Number(aggregate.sessions || 0))),
    pageViews: Math.max(0, Math.round(Number(aggregate.pageViews || 0))),
    registrationStarts: Math.max(0, Math.round(Number(aggregate.registrationStarts || 0))),
    registrationCompletes: Math.max(0, Math.round(Number(aggregate.registrationCompletes || 0))),
    firstActionUsers: Math.max(0, Math.round(Number(aggregate.firstActionUsers || 0))),
    conversions: Math.max(0, Math.round(Number(aggregate.conversions || 0))),
    errors: Math.max(0, Math.round(Number(aggregate.errors || 0))),
    registrationComplete: Boolean(aggregate.registrationComplete),
    firstAction: Boolean(aggregate.firstAction),
    processed: false,
    createdAt: serverTimestamp(),
    expiresAt: Timestamp.fromMillis(now + ANALYTICS_V2_INTAKE_RETENTION_DAYS * 24 * 60 * 60 * 1000),
  };

  const cohortToken = sanitizeCohortToken(aggregate.cohortToken);
  if (cohortToken && (docPayload.registrationComplete || docPayload.firstAction)) {
    docPayload.cohortToken = cohortToken;
  }

  if (!docPayload.sessionId) docPayload.sessionId = "anonymous";
  return docPayload;
};

export const buildAnalyticsDailyV2Patch = (event = {}, { includeSessionStart = false } = {}) => {
  const aggregate = aggregateEvent(createEmptyAggregate(getDateId(event.createdAt || new Date())), event, { includeSessionStart });
  return aggregateToPatch(aggregate);
};

const shouldDedupePageView = (event, storage, now) => {
  if (event.eventName !== "page_view") return false;
  const key = `${event.path || "home"}|${event.sessionId}`;
  const previous = readJson(storage, ANALYTICS_V2_LAST_PAGE_VIEW_KEY, null);
  if (previous?.key === key && now - Number(previous.at || 0) < ANALYTICS_V2_PAGE_VIEW_DEDUPE_MS) {
    return true;
  }
  writeJson(storage, ANALYTICS_V2_LAST_PAGE_VIEW_KEY, { key, at: now });
  return false;
};

const getDedupeIdentity = (session = {}) => auth.currentUser?.uid || session.id || "anonymous";

const shouldDedupeSingletonEvent = (event, session, storage, dateId) => {
  if (!["registration_complete", "first_action"].includes(event.eventName)) return false;
  const dedupe = readJson(storage, ANALYTICS_V2_DEDUPE_KEY, {});
  const identity = getDedupeIdentity(session);
  const key = `${event.eventName}|${identity}|${dateId}`;
  if (dedupe[key]) return true;
  writeJson(storage, ANALYTICS_V2_DEDUPE_KEY, {
    ...dedupe,
    [key]: true,
  });
  return false;
};

const hasAggregateCounts = (aggregate = {}) =>
  Boolean(
    aggregate.visitors ||
      aggregate.sessions ||
      aggregate.pageViews ||
      aggregate.registrationStarts ||
      aggregate.registrationCompletes ||
      aggregate.firstActionUsers ||
      aggregate.conversions ||
      aggregate.errors ||
      Object.keys(aggregate.pages || {}).length ||
      Object.keys(aggregate.sources || {}).length ||
      Object.keys(aggregate.campaigns || {}).length ||
      Object.keys(aggregate.features || {}).length ||
      Object.keys(aggregate.activation || {}).length ||
      Object.keys(aggregate.errorsByCode || {}).length
  );

const readPendingAggregates = (storage) => readJson(storage, ANALYTICS_V2_PENDING_KEY, {});
const writePendingAggregates = (storage, pending) => writeJson(storage, ANALYTICS_V2_PENDING_KEY, pending);

const addPendingEvent = (storage, event, options) => {
  const dateId = getDateId(event.createdAt);
  const pending = readPendingAggregates(storage);
  const aggregate = pending[dateId] || createEmptyAggregate(dateId);
  aggregateEvent(aggregate, event, options);
  pending[dateId] = aggregate;
  writePendingAggregates(storage, pending);
  return aggregate;
};

let analyticsV2FlushTimer = null;
let analyticsV2ListenersInstalled = false;

export const flushAnalyticsV2Events = async ({
  storage = typeof window !== "undefined" ? window.sessionStorage : null,
  now = getNow(),
} = {}) => {
  if (typeof window === "undefined" || !storage) return false;
  if (analyticsV2FlushTimer) {
    clearTimeout(analyticsV2FlushTimer);
    analyticsV2FlushTimer = null;
  }

  const pending = readPendingAggregates(storage);
  const entries = Object.entries(pending).filter(([, aggregate]) => hasAggregateCounts(aggregate));
  if (entries.length === 0) return false;

  writePendingAggregates(storage, {});
  const session = readSession(storage) || {};
  try {
    await Promise.all(
      entries.map(([dateId, aggregate]) =>
        addDoc(
          collection(db, ANALYTICS_V2_INTAKE_COLLECTION),
          buildAnalyticsIntakeV2Document({ ...aggregate, date: dateId }, { session, now })
        )
      )
    );
    return true;
  } catch (error) {
    writePendingAggregates(storage, pending);
    if (process.env.NODE_ENV !== "production") {
      console.warn("Analytics V2 batch was not recorded:", error?.code || error?.message);
    }
    return false;
  }
};

const scheduleAnalyticsV2Flush = (storage) => {
  if (typeof window === "undefined") return;
  if (analyticsV2FlushTimer) clearTimeout(analyticsV2FlushTimer);
  analyticsV2FlushTimer = setTimeout(() => {
    flushAnalyticsV2Events({ storage });
  }, ANALYTICS_V2_FLUSH_DEBOUNCE_MS);
};

const installAnalyticsV2FlushListeners = (storage) => {
  if (typeof window === "undefined" || analyticsV2ListenersInstalled) return;
  analyticsV2ListenersInstalled = true;

  const flushBestEffort = () => {
    flushAnalyticsV2Events({ storage });
  };

  document?.addEventListener?.("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushBestEffort();
  });
  window.addEventListener("pagehide", flushBestEffort);
};

export const recordAnalyticsEvent = async (eventName, payload = {}, options = {}) => {
  if (typeof window === "undefined") return false;
  if (!CANONICAL_ANALYTICS_EVENTS.includes(eventName)) return false;

  const now = options.now || getNow();
  const storage = options.storage || window.sessionStorage;
  installAnalyticsV2FlushListeners(storage);

  const session = getAnalyticsV2Session({ ...options, now, storage });
  const event = buildCanonicalAnalyticsEvent(eventName, payload, { ...options, now, session });
  if (!event || shouldDedupePageView(event, storage, now)) return false;

  const dateId = getDateId(event.createdAt);
  if (shouldDedupeSingletonEvent(event, session, storage, dateId)) return false;

  const countedDates = session.countedDates || {};
  const includeSessionStart = !countedDates[dateId];
  if (event.eventName === "session_start" && !includeSessionStart) return false;

  if (["registration_complete", "first_action"].includes(event.eventName)) {
    event.cohortToken = await getActivationCohortToken(options);
  }

  const aggregate = addPendingEvent(storage, event, { includeSessionStart });
  writeSession(storage, {
    ...session,
    countedDates: {
      ...countedDates,
      [dateId]: true,
    },
    lastSeenAt: now,
  });

  if (options.flush === "immediate" || Number(aggregate.eventCount || 0) >= ANALYTICS_V2_MAX_PENDING_EVENTS) {
    return flushAnalyticsV2Events({ storage });
  }

  scheduleAnalyticsV2Flush(storage);
  return true;
};

const addNestedCounts = (target, source, metric) => {
  Object.entries(source || {}).forEach(([key, value]) => {
    const amount = Number(value?.[metric] || value || 0);
    if (!Number.isFinite(amount)) return;
    target[key] = (target[key] || 0) + amount;
  });
};

export const buildActivationCohortAnalytics = ({
  dailyRollups = [],
  users = [],
  events = [],
  period = {},
} = {}) => {
  const aggregateSignupUsers = dailyRollups.reduce(
    (total, day) => total + Number(day?.activation?.signupUsers || day?.registrationCompletes || 0),
    0
  );
  const aggregateFirstActionUsers = dailyRollups.reduce(
    (total, day) =>
      total + Number(day?.activation?.activated7d || day?.activation?.firstActionUsers || day?.firstActionUsers || 0),
    0
  );

  if (aggregateSignupUsers || aggregateFirstActionUsers) {
    return {
      numerator: aggregateFirstActionUsers,
      denominator: aggregateSignupUsers,
      percentage: aggregateSignupUsers ? Math.round((aggregateFirstActionUsers / aggregateSignupUsers) * 100) : 0,
      rangeLabel: period.label || "Selected range",
      source: "analyticsDailyV2",
    };
  }

  const startMs = Number.isFinite(period.startMs) ? period.startMs : 0;
  const endMs = Number.isFinite(period.endMs) ? period.endMs : Number.MAX_SAFE_INTEGER;
  const signupUsers = users.filter((user) => {
    const created = toMillis(user.createdAt || user.termsAcceptedAt || user.createdOn);
    return created >= startMs && created <= endMs;
  });
  const signupUserIds = new Set(signupUsers.map((user) => user.id || user.uid).filter(Boolean));
  const firstActionUserIds = new Set(
    events
      .filter((event) => {
        const created = toMillis(event.createdAt);
        return created >= startMs && created <= endMs;
      })
      .filter((event) => LEGACY_FIRST_ACTION_EVENTS.includes(event.eventType || event.eventName))
      .map((event) => event.userId)
      .filter((userId) => signupUserIds.has(userId))
  );
  const denominator = signupUserIds.size;
  const numerator = firstActionUserIds.size;
  return {
    numerator,
    denominator,
    percentage: denominator ? Math.round((numerator / denominator) * 100) : 0,
    rangeLabel: period.label || "Selected range",
    source: "legacy-fallback",
  };
};

export const buildCanonicalAnalyticsDashboard = ({
  dailyRollups = [],
  users = [],
  events = [],
  period = {},
} = {}) => {
  const totals = {
    visitors: 0,
    sessions: 0,
    pageViews: 0,
    registrationStarts: 0,
    registrationCompletes: 0,
    firstActionUsers: 0,
    conversions: 0,
    errors: 0,
  };
  const pages = {};
  const sources = {};
  const campaigns = {};
  const features = {};

  dailyRollups.forEach((day) => {
    Object.keys(totals).forEach((key) => {
      const value = Number(day?.[key] || 0);
      if (Number.isFinite(value)) totals[key] += value;
    });
    addNestedCounts(pages, day?.pages, "pageViews");
    addNestedCounts(sources, day?.sources, "sessions");
    addNestedCounts(campaigns, day?.campaigns, "visits");
    addNestedCounts(features, day?.features, "opens");
  });

  if (!sources.direct) sources.direct = 0;

  return {
    ...totals,
    pages: Object.entries(pages).map(([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count),
    sources: Object.entries(sources)
      .map(([label, count]) => ({ label: label === "unknown" ? "direct" : label, count }))
      .reduce((items, item) => {
        const existing = items.find((candidate) => candidate.label === item.label);
        if (existing) existing.count += item.count;
        else items.push(item);
        return items;
      }, [])
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)),
    campaigns: Object.entries(campaigns)
      .filter(([label]) => label && label !== "unknown")
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)),
    features: Object.entries(features).map(([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count),
    activation: buildActivationCohortAnalytics({ dailyRollups, users, events, period }),
    retention: {
      rawEventRetentionDays: ANALYTICS_V2_RAW_RETENTION_DAYS,
      ttlEnabled: false,
    },
  };
};

const countLegacySource = (events = [], label = "") =>
  events.filter((event) => {
    const source = String(event.utm_source || "").trim().toLowerCase();
    if (label === "direct") return !source || source === "direct" || source === "unknown";
    if (label === "campaign") return Boolean(event.utm_campaign || source) && source !== "direct" && source !== "unknown";
    return source === label;
  }).length;

export const buildV2LegacyComparison = ({
  analyticsV2 = {},
  legacyTrafficEvents = [],
  legacyPublicVisitorAnalytics = {},
  legacyActivation = {},
  period = {},
} = {}) => {
  const v2Direct = (analyticsV2.sources || []).find((item) => item.label === "direct")?.count || 0;
  const v2Campaign = (analyticsV2.sources || []).find((item) => item.label === "campaign")?.count || 0;

  return {
    periodLabel: period.label || analyticsV2.activation?.rangeLabel || "Selected range",
    rows: [
      {
        label: "Visitors",
        v2: analyticsV2.visitors || 0,
        legacy: legacyPublicVisitorAnalytics.publicVisitors ?? "n/a",
        note: "V2 counts one session visitor per day; legacy visitor analytics estimates anonymous public visitors from raw campaignTrafficEvents.",
      },
      {
        label: "Sessions",
        v2: analyticsV2.sessions || 0,
        legacy: legacyPublicVisitorAnalytics.funnel?.publicVisitors ?? "n/a",
        note: "V2 has an explicit session counter; legacy uses public visitor session IDs only.",
      },
      {
        label: "Page views",
        v2: analyticsV2.pageViews || 0,
        legacy: legacyPublicVisitorAnalytics.publicPageViews ?? "n/a",
        note: "V2 counts canonical page_view events; legacy counts public_page_view event documents.",
      },
      {
        label: "Direct traffic",
        v2: v2Direct,
        legacy: countLegacySource(legacyTrafficEvents, "direct"),
        note: "V2 normalizes missing source to direct. Legacy may store missing values as unknown.",
      },
      {
        label: "Campaign traffic",
        v2: v2Campaign,
        legacy: countLegacySource(legacyTrafficEvents, "campaign"),
        note: "V2 uses source=campaign sessions; legacy campaign traffic comes from UTM event documents.",
      },
      {
        label: "Registration completes",
        v2: analyticsV2.registrationCompletes || 0,
        legacy: legacyTrafficEvents.filter((event) => event.eventType === "registration_completed").length,
        note: "V2 is daily aggregate-based; legacy is raw event-document based.",
      },
      {
        label: "Activation",
        v2: `${analyticsV2.activation?.percentage || 0}% (${analyticsV2.activation?.numerator || 0}/${analyticsV2.activation?.denominator || 0})`,
        legacy: `${legacyActivation.percentage || 0}% (${legacyActivation.numerator || 0}/${legacyActivation.denominator || 0})`,
        note: "V2 uses deduped aggregate signup and first-action counters; legacy fallback joins user docs to analyticsEvents.",
      },
    ],
  };
};

export const buildAnalyticsBackfillDryRunReport = ({
  campaignTrafficEvents = [],
  analyticsEvents = [],
  analyticsDaily = [],
  fallCrawlCollectionsAvailable = false,
} = {}) => {
  const dateSet = new Set();
  [...campaignTrafficEvents, ...analyticsEvents].forEach((event) => {
    const millis = toMillis(event.createdAt);
    if (millis) dateSet.add(getDateId(new Date(millis)));
  });
  analyticsDaily.forEach((day) => {
    if (day?.date) dateSet.add(day.date);
    else if (day?.id) dateSet.add(day.id);
  });

  return {
    datesSeen: [...dateSet].sort(),
    reliable: [
      "Legacy analyticsDaily can reconstruct coarse daily totals such as visits, signups, request starts/completions, stand/product/job/community creates when those fields exist.",
      "campaignTrafficEvents can reconstruct raw event-document counts, UTM/source/campaign/path counts, and public visitor estimates for loaded raw-event ranges.",
      "analyticsEvents can reconstruct logged-in registration/activation event-document counts where userId exists.",
      "Fall Crawl event-day collections can reconstruct verified stamp and giveaway metrics from their authoritative event documents.",
    ],
    notClean: [
      "Legacy analyticsDaily cannot reconstruct unique visitors, sessions, source attribution, feature usage detail, or deduped page views.",
      "campaignTrafficEvents cannot perfectly reconstruct V2 page keys where legacy paths contain arbitrary dynamic segments or duplicate route emissions.",
      "analyticsEvents cannot produce privacy-preserving long-term per-user activation cohorts without temporary dedupe or aggregation.",
      fallCrawlCollectionsAvailable
        ? "Fall Crawl interaction counts can be rebuilt for event collections that still have the authoritative documents."
        : "Fall Crawl interaction collections were not loaded in this dry run, so event-specific historical reconstruction must be verified separately.",
    ],
  };
};

export const ANALYTICS_V2_LEGACY_RETIREMENT_PLAN = [
  {
    tracker: "CampaignTrafficTracker public page/session writer",
    legacyWrites: "campaignTrafficEvents public_session_started and public_page_view",
    dependsOn: "Traffic Snapshot, Visitor Analytics, Fall Crawl interaction detail during comparison",
    order: 1,
  },
  {
    tracker: "CampaignTrafficTracker usage event writer",
    legacyWrites: "campaignTrafficEvents feature, funnel, stuck/error usage documents",
    dependsOn: "Traffic Snapshot feature/funnel/error cards",
    order: 2,
  },
  {
    tracker: "CampaignTrafficTracker campaign visit writer",
    legacyWrites: "campaignTrafficEvents campaign_visit and stored attribution writes",
    dependsOn: "Campaign link attribution and legacy Top Sources/Top Campaigns",
    order: 3,
  },
  {
    tracker: "appAnalytics raw event writer",
    legacyWrites: "analyticsEvents signup, activation, page_view, onboarding, profile events",
    dependsOn: "First 5 Minutes/User Journey analytics and legacy activation fallback",
    order: 4,
  },
  {
    tracker: "lowCostAnalytics daily rollup",
    legacyWrites: "analyticsDaily daily counters",
    dependsOn: "Visitor Analytics summary mode and long-range legacy dashboards",
    order: 5,
  },
];
